import type { Aabb2, Point2 } from '../geometry/point';
import { normalizeQuarterTurns } from './types';
import type { ViewportState } from './types';

/**
 * Rotates the view by whole quarter turns about a world-space pivot.
 *
 * The pivot is the drawing's centre, not the container's, and the current centre is
 * carried around it — so a user who has panned into a corner and then rotates finds
 * that corner where they expect it, rather than being thrown back to the middle of
 * the drawing. This matches `rotate2DView` in the CAD viewer's renderer, which
 * rotates its pan offset about the geometry centre for the same reason.
 *
 * `delta` is in clockwise quarter turns: `1` clockwise, `-1` counter-clockwise.
 */
export const rotateViewport = (
  viewport: ViewportState,
  delta: number,
  pivot: Point2 | null,
): ViewportState => {
  const quarterTurns = normalizeQuarterTurns(viewport.quarterTurns + delta);
  if (quarterTurns === viewport.quarterTurns) {
    return viewport;
  }
  if (!pivot) {
    return { ...viewport, quarterTurns };
  }

  const [pivotX, pivotY] = pivot;
  const [centerX, centerY] = viewport.center;
  const offsetX = centerX - pivotX;
  const offsetY = centerY - pivotY;

  // Content turning clockwise on screen means the centre travels
  // counter-clockwise in world space, since world y is up and screen y is down.
  const turns = normalizeQuarterTurns(delta);
  const rotated: Point2 =
    turns === 0
      ? [offsetX, offsetY]
      : turns === 1
      ? [-offsetY, offsetX]
      : turns === 2
      ? [-offsetX, -offsetY]
      : [offsetY, -offsetX];

  return {
    ...viewport,
    quarterTurns,
    center: [pivotX + rotated[0], pivotY + rotated[1]],
  };
};

/** Centre of an AABB, or `null` for absent bounds. Used as the rotation pivot. */
export const aabbCenter = (bounds: Aabb2 | null | undefined): Point2 | null =>
  bounds ? [(bounds[0] + bounds[2]) / 2, (bounds[1] + bounds[3]) / 2] : null;

/** Union of two AABBs; either may be absent. */
export const unionAabb = (
  a: Aabb2 | null | undefined,
  b: Aabb2 | null | undefined,
): Aabb2 | null => {
  if (!a) {
    return b ?? null;
  }
  if (!b) {
    return a;
  }
  return [
    Math.min(a[0], b[0]),
    Math.min(a[1], b[1]),
    Math.max(a[2], b[2]),
    Math.max(a[3], b[3]),
  ];
};
